(() => {
  const {
    parseJson,
    escapeHtml,
    escapeAttr,
    formatMoney,
    formatThemeMoney,
    formatDelta,
  } = window.Express.productBuilderUtils;
  const {
    createDefaultSelections,
    createBuilderIndexes,
    getIndexedGroup,
    getIndexedGroupIndex,
    getIndexedOption,
    getIndexedOptionIndex,
    collectSelectedOptions,
    toggleOption,
    hasRequiredSelections,
    normalizeSelections,
  } = window.Express.productBuilderState;
  const {
    getGalleryPreviewSlides: buildGalleryPreviewSlides,
    getOptionVisualImage,
    getProductPreviewImage,
    getProductThumbnailUrl,
    getThumbnail,
  } = window.Express.productBuilderMedia;
  const {
    buildProperties,
    createBuilderSnapshot,
    createSelectedOptionsSnapshot,
    getOptionPriceDeltaCents,
    getSelectedOptionComponentSet,
    getSelectedOptionsTotal,
  } = window.Express.productBuilderProperties;

  const CONFIG_NOTES_DEFAULT = 'Include GST';
  const MISSING_BUILDER_CONFIG_MESSAGE = 'This product does not have a published builder configuration.';
  const CHECKMARK_ICON = `
    <svg
      class="icon icon-checkmark"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      width="24"
      height="24"
    >
      <path d="M20 6 9 17l-5-5"/>
    </svg>
  `;

  function getOptionDisplayPrice(option = {}, group = null) {
    const optionWithComponentSets = {
      ...option,
      optionComponentSets: Array.isArray(option.optionComponentSets)
        ? option.optionComponentSets
        : (Array.isArray(group?.optionComponentSets) ? group.optionComponentSets : []),
    };
    const priceDeltaCents = getOptionPriceDeltaCents(optionWithComponentSets);
    const hasComponentSet = Boolean(getSelectedOptionComponentSet(optionWithComponentSets));

    return {
      priceDeltaCents,
      included: hasComponentSet ? priceDeltaCents === 0 : option.included,
    };
  }

  const CHEVRON_LEFT = `
    <svg
      class="icon"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      width="24"
      height="24"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  `;

  const CHEVRON_RIGHT = `
    <svg
      class="icon"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      width="24"
      height="24"
    >
      <path d="m9 18 6-6-6-6"/>
    </svg>
  `;

  const CHEVRON_UP = `
    <svg
      class="icon"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      width="24"
      height="24"
    >
      <path d="m18 15-6-6-6 6"/>
    </svg>
  `;

  const CHEVRON_DOWN = `
    <svg
      class="icon"
      aria-hidden="true"
      focusable="false"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      width="24"
      height="24"
    >
      <path d="m6 9 6 6 6-6"/>
    </svg>
  `;

  let builderInstanceCounter = 0;

  function getPreviewHelper() {
    return window.Express.productBuilderPreview;
  }

  function getDisclosureHelper() {
    return window.Express.productBuilderDisclosure;
  }

  function getDragScrollHelper() {
    return window.Express.productBuilderDragScroll;
  }

  function getNavScrollHelper() {
    return window.Express.productBuilderNavScroll;
  }

  function getGalleryHelper() {
    return window.Express.productBuilderGallery;
  }

  function getActiveGroupIndex(groups, activeGroupKey, indexes = null) {
    const indexedPosition = getIndexedGroupIndex(indexes, activeGroupKey);
    return Math.max(0, indexedPosition >= 0 ? indexedPosition : groups.findIndex((group) => group.groupTypeKey === activeGroupKey));
  }

  function isSelected(selections, group, optionKey) {
    return (selections[group.groupTypeKey] || []).includes(optionKey);
  }

  function getSelectedOptionIndex(group, selections, indexes = null) {
    const selectedKey = (selections[group.groupTypeKey] || [])[0] || '';
    const indexedPosition = getIndexedOptionIndex(indexes, group.groupTypeKey, selectedKey);
    return indexedPosition >= 0 ? indexedPosition : (group.options || []).findIndex((option) => option.key === selectedKey);
  }

  function getSelectedOption(group, selections, indexes = null) {
    const selectedKey = (selections[group.groupTypeKey] || [])[0] || '';
    return getIndexedOption(indexes, group.groupTypeKey, selectedKey)
      || (group.options || []).find((option) => option.key === selectedKey)
      || null;
  }

  function getInputType(group) {
    const inputType = String(group?.inputType || 'image_card').toLowerCase();
    if (inputType === 'image_gallery') return 'gallery';
    if (inputType === 'swatch') return 'swatch';
    if (inputType === 'pill') return 'pill';
    if (inputType === 'checkbox') return 'checkbox';
    if (inputType === 'select') return 'select';
    if (inputType === 'gallery') return 'gallery';
    return 'image_card';
  }

  function syncHiddenInputs(form, properties) {
    if (!form) return;

    form.querySelectorAll('[data-builder-hidden-property="true"]').forEach((node) => node.remove());

    Object.entries(properties || {}).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.value = typeof value === 'object' ? JSON.stringify(value) : String(value);
      input.setAttribute('data-builder-hidden-property', 'true');
      form.appendChild(input);
    });
  }

  function updateThemePrice(root, total) {
    const sectionId =
      root.dataset.productSectionId ||
      root.productForm?.closest('[data-section]')?.dataset.section ||
      root.dataset.sectionId;

    if (!sectionId) return;

    const priceRoot = document.querySelector(`#price-${sectionId} .price`);
    if (!priceRoot) return;

    const finalPriceSpan = priceRoot.classList.contains('price--on-sale')
      ? priceRoot.querySelector('.price__sale .price-item.price-item--sale')
      : priceRoot.querySelector('.price__regular .price-item.price-item--regular');
    const compareAtSpan = priceRoot.querySelector('.price__sale .price-item.price-item--regular');
    const currentPrice = Number(String(finalPriceSpan?.textContent || '').replace(/[^\d.]/g, '')) || 0;
    const currentCompareAtPrice = Number(String(compareAtSpan?.textContent || '').replace(/[^\d.]/g, '')) || 0;
    const compareAtDifference = Math.max(0, currentCompareAtPrice - currentPrice);
    const themeTotal = (Number(total) || 0) / 100;

    if (finalPriceSpan) finalPriceSpan.textContent = formatThemeMoney(themeTotal);
    if (compareAtSpan) compareAtSpan.textContent = formatThemeMoney(themeTotal + compareAtDifference);
  }

  function getCartInterface() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function getCartSections(cart) {
    if (!cart || typeof cart.getSectionsToRender !== 'function') return [];
    return cart.getSectionsToRender().map((section) => section.id);
  }

  function parseQuantity(value, fallback = 1) {
    const quantity = Number(value);
    if (Number.isFinite(quantity)) return quantity;

    return fallback;
  }

  function parseCartQuantity(value, fallback = 1) {
    const quantity = Number(value);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : fallback;
  }

  function getFormQuantityInput(form) {
    if (!form) return null;

    if (form.elements?.quantity) return form.elements.quantity;
    if (form.id) return document.querySelector(`[form="${form.id}"][name="quantity"]`);

    return form.querySelector('input[name="quantity"]');
  }

  function waitForImage(image) {
    if (!image || image.complete) return Promise.resolve();
    if (typeof image.decode === 'function') {
      return image.decode().catch(() => undefined);
    }
    return new Promise((resolve) => {
      image.addEventListener('load', resolve, { once: true });
      image.addEventListener('error', resolve, { once: true });
    });
  }

  class ExpressProductBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.builderInitialized === 'true') return;
      this.dataset.builderInitialized = 'true';

      this.mountTarget = this.querySelector('[data-product-builder-app]') || this;
      this.mountTarget.innerHTML = '<div class="ep-product-builder-status">Loading builder...</div>';
      this.classList.add('is-loading-assets');

      const configNode = this.querySelector('[data-product-builder-config]');
      this.config = configNode ? parseJson(configNode.textContent || '') : null;

      if (!this.config) {
        this.mountTarget.innerHTML = '<div class="ep-product-builder-status is-error">Unable to load the builder.</div>';
        return;
      }

      this.indexes = createBuilderIndexes(this.config);
      this.gallerySlidesCache = new Map();
      this.productForm = this.resolveProductForm();
      if (this.productForm) {
        const formSectionId =
          this.productForm.closest('[data-section]')?.dataset.section ||
          this.productForm.closest('[data-section-id]')?.dataset.sectionId ||
          '';
        if (this.productForm.id) {
          this.dataset.productFormId = this.productForm.id;
        }
        if (formSectionId) {
          this.dataset.productSectionId = formSectionId;
        }
      }

      this.headerLogoHtml = this.getHeaderLogoHtml();
      if (!this.dataset.builderInstanceId) {
        builderInstanceCounter += 1;
        this.dataset.builderInstanceId = `epb-${builderInstanceCounter}`;
      }
      this.state = {
        activeGroupKey: this.config.groups?.[0]?.groupTypeKey || '',
        isSubmitting: false,
        selections: createDefaultSelections(this.config),
        errorMessage: '',
        renderMode: 'init',
        submittingAction: '',
        lastPreviewOption: null,
        lastPreviewUrl: '',
        previewImageIndex: 0,
        lastTotal: -1,
        loaded: false,
        assetsLoaded: false,
        navAnimationGroupKey: null,
        footerSumlinesExpanded: false,
        galleryPreviewOptionKeys: {},
      };

      this.bindBuilderEvents();
      this.render();
      this.dispatchSelectionChange();
      this.waitForInitialAssets();
    }

    resolveProductForm() {
      const explicitId = this.dataset.productFormId || '';
      if (explicitId) {
        const explicitForm = document.getElementById(explicitId);
        if (explicitForm) return explicitForm;
      }

      const productInfo = document.querySelector('product-info');
      const formInProductInfo = productInfo?.querySelector('form[data-type="add-to-cart-form"]');
      if (formInProductInfo) return formInProductInfo;

      return document.querySelector('form[data-type="add-to-cart-form"]');
    }

    getHeaderLogoHtml() {
      const logo = document.querySelector('.section-header .header__heading-logo, header .header__heading-logo, .header__heading-logo');
      if (!logo) return '';

      const clone = logo.cloneNode(true);
      clone.classList.add('epb__brand-logo');
      clone.removeAttribute('id');

      return clone.outerHTML;
    }

    getFooterSumlinesId() {
      return `${this.dataset.builderInstanceId || 'epb'}-footer-sumlines`;
    }

    syncFooterSumlinesDisclosure() {
      const expanded = Boolean(this.state.footerSumlinesExpanded);

      getDisclosureHelper().setDisclosureState({
        button: this.mountTarget.querySelector('[data-footer-sumlines-toggle]'),
        panel: this.mountTarget.querySelector('.epb__footer-sumlines'),
        expanded,
        expandedIcon: CHEVRON_DOWN,
        collapsedIcon: CHEVRON_UP,
        expandedLabel: 'Hide summary items',
        collapsedLabel: 'Show summary items',
      });
    }

    getGalleryPreviewSlides(group, previewHelper) {
      const cacheKey = group?.groupTypeKey || '';
      if (!cacheKey) return buildGalleryPreviewSlides(this.config, group, previewHelper);
      if (!this.gallerySlidesCache) this.gallerySlidesCache = new Map();
      if (!this.gallerySlidesCache.has(cacheKey)) {
        this.gallerySlidesCache.set(cacheKey, buildGalleryPreviewSlides(this.config, group, previewHelper));
      }
      return this.gallerySlidesCache.get(cacheKey) || [];
    }

    bindDraggableNavigation() {
      getDragScrollHelper().attach(this.mountTarget.querySelector('.epb__navlist'), {
        maxWidth: 1499,
      });
    }

    syncNavScroll(options = {}) {
      const helper = getNavScrollHelper();
      if (!helper || typeof helper.attach !== 'function') return;

      const controller = helper.attach(this.mountTarget);
      if (options.centerActive && controller && typeof controller.centerActive === 'function') {
        controller.centerActive();
      } else if (controller && typeof controller.syncControls === 'function') {
        controller.syncControls();
      }
    }

    syncGalleryState(context) {
      if (context.activeInputType !== 'gallery') return;

      getGalleryHelper().attach(this.mountTarget, context, {
        onNavigate: (direction) => this.navigateGalleryOption(direction),
      });
    }

    isQuoteEditMode() {
      return this.dataset.builderMode === 'quote-edit';
    }

    createBuilderSnapshot() {
      return createBuilderSnapshot(this.config, this.state.selections);
    }

    getSelectedOptionsSnapshot() {
      const groups = this.config?.groups || [];
      const selectedOptions = collectSelectedOptions(groups, this.state?.selections || {}, this.indexes);
      const total = getSelectedOptionsTotal(this.config, selectedOptions);

      return createSelectedOptionsSnapshot(this.config, this.state?.selections || {}, selectedOptions, total);
    }

    dispatchSelectionChange() {
      this.dispatchEvent(new CustomEvent('product-builder:selection-change', {
        bubbles: true,
        detail: {
          snapshot: this.getSelectedOptionsSnapshot(),
        },
      }));
    }

    dispatchConfigurationSaved(input = {}) {
      const snapshot = createSelectedOptionsSnapshot(
        this.config,
        this.state?.selections || {},
        input.selectedOptions || [],
        input.total
      );

      this.dispatchEvent(new CustomEvent('product-builder:configuration-saved', {
        bubbles: true,
        detail: {
          snapshot,
          selections: snapshot.selections,
          selectedOptions: snapshot.selectedOptions,
          groupKeys: snapshot.selectedOptions.map((option) => option.groupKey).filter(Boolean),
          optionKeys: snapshot.selectedOptions.map((option) => option.key).filter(Boolean),
          properties: input.properties || {},
        },
      }));
    }

    getQuoteLineItem(input = {}) {
      const groups = this.config.groups || [];
      const selectedOptions = collectSelectedOptions(groups, this.state.selections, this.indexes);
      const total = getSelectedOptionsTotal(this.config, selectedOptions);
      const properties = input.properties || buildProperties(this.config, selectedOptions, total);

      return {
        id: input.variantId || this.config.product?.baseVariantId || this.getVariantId(),
        product_id: this.config.product?.id || this.dataset.productId || '',
        title: this.config.product?.title || this.config.title || '',
        product_title: this.config.product?.title || this.config.title || '',
        handle: this.config.product?.handle || '',
        image: this.config.product?.product_image || null,
        quantity: input.quantity || this.getQuantity(),
        sku: this.config.product?.sku || '',
        price: Number(this.config.product?.basePriceCents || 0),
        final_price: Number(this.config.product?.basePriceCents || 0),
        properties,
        builder: this.createBuilderSnapshot(),
      };
    }

    setSelections(selections = {}) {
      this.state.selections = normalizeSelections(this.config, selections, this.indexes);
      this.state.errorMessage = '';
      this.state.renderMode = 'selection';
      this.render();
      this.dispatchSelectionChange();
    }

    async buildQuoteSubmitPayload() {
      const groups = this.config.groups || [];
      const selectedOptions = collectSelectedOptions(groups, this.state.selections, this.indexes);
      const total = getSelectedOptionsTotal(this.config, selectedOptions);

      if (this.state.isSubmitting) return null;
      if (!hasRequiredSelections(groups, this.state.selections)) {
        this.state.errorMessage = 'Select the required options before continuing.';
        this.render();
        return null;
      }

      this.state.isSubmitting = true;
      this.state.submittingAction = 'add-to-quote';
      this.state.errorMessage = '';
      this.render();

      const properties = buildProperties(this.config, selectedOptions, total);
      syncHiddenInputs(this.productForm, properties);

      return {
        id: this.config.product?.baseVariantId || this.getVariantId(),
        quantity: this.getQuantity(),
        properties,
        redirectUrl: this.config.quote?.redirectUrl || this.dataset.quotePageUrl || '/pages/request-a-quote',
      };
    }

    async submitExternalQuote() {
      try {
        const payload = await this.buildQuoteSubmitPayload();

        if (!payload) return;

        await this.submitQuote({
          variantId: payload.id,
          quantity: payload.quantity,
          properties: payload.properties,
          redirectUrl: payload.redirectUrl,
        });
      } catch (error) {
        this.state.isSubmitting = false;
        this.state.submittingAction = '';
        this.state.errorMessage = error instanceof Error
          ? error.message
          : 'Unable to add this configuration to the quote.';
        this.render();
      }
    }

    navigateToGroup(groupKey) {
      const groups = this.config.groups || [];
      const group = getIndexedGroup(this.indexes, groupKey) || groups.find((entry) => entry.groupTypeKey === groupKey);
      if (!group) return;

      this.state.activeGroupKey = group.groupTypeKey;
      delete this.state.galleryPreviewOptionKeys[this.state.activeGroupKey];
      this.state.navAnimationGroupKey = this.state.activeGroupKey;
      this.state.errorMessage = '';
      this.state.renderMode = 'section';
      this.renderActiveGroupState();
    }

    bindBuilderEvents() {
      this.mountTarget.addEventListener('click', (event) => {
        const navButton = event.target.closest('[data-nav]');
        if (navButton) {
          this.navigateToGroup(navButton.dataset.nav || '');
          return;
        }

        const summaryNavButton = event.target.closest('[data-summary-nav]');
        if (summaryNavButton) {
          this.navigateToGroup(summaryNavButton.dataset.summaryNav || '');
          return;
        }

        const optionButton = event.target.closest('[data-group][data-option]');
        if (optionButton) {
          const groups = this.config.groups || [];
          const group = getIndexedGroup(this.indexes, optionButton.dataset.group) || groups.find((entry) => entry.groupTypeKey === optionButton.dataset.group);
          const optionKey = optionButton.dataset.option || '';
          if (!group || !optionKey) return;
          toggleOption(group, optionKey, this.state.selections);
          if (getInputType(group) === 'gallery') {
            this.state.galleryPreviewOptionKeys[group.groupTypeKey] = optionKey;
            this.state.previewImageIndex = Math.max(0, getIndexedOptionIndex(this.indexes, group.groupTypeKey, optionKey));
          }
          this.state.errorMessage = '';
          this.state.renderMode = 'selection';
          this.renderSelectionState();
          this.dispatchSelectionChange();
          return;
        }

        const footerSumlinesToggle = event.target.closest('[data-footer-sumlines-toggle]');
        if (footerSumlinesToggle) {
          this.state.footerSumlinesExpanded = getDisclosureHelper().toggle(this.state.footerSumlinesExpanded);
          this.syncFooterSumlinesDisclosure();
          return;
        }

        const navArrow = event.target.closest('[data-nav-dir]');
        if (navArrow) {
          const direction = navArrow.dataset.navDir === 'next' ? 1 : -1;
          const groups = this.config.groups || [];
          const activeGroupIndex = getActiveGroupIndex(groups, this.state.activeGroupKey, this.indexes);
          const nextGroup = groups[activeGroupIndex + direction];
          if (!nextGroup) return;
          this.navigateToGroup(nextGroup.groupTypeKey);
          return;
        }

        const optionPreviewArrow = event.target.closest('[data-option-preview-dir]');
        if (optionPreviewArrow) {
          const direction = optionPreviewArrow.dataset.optionPreviewDir === 'next' ? 1 : -1;
          this.navigateGalleryOption(direction);
          return;
        }

        const submitButton = event.target.closest('[data-submit]');
        if (submitButton) {
          const action = submitButton.dataset.submitAction || 'add-to-quote';
          this.handleSubmitAction(action).catch((error) => console.error(error));
          return;
        }

        const cancelButton = event.target.closest('[data-builder-cancel]');
        if (cancelButton) {
          if (this.isQuoteEditMode()) {
            this.dispatchEvent(new CustomEvent('product-builder:quote-edit-cancel', {
              bubbles: true,
            }));
            return;
          }

          this.closeModal();
        }
      });

      this.mountTarget.addEventListener('change', (event) => {
        const select = event.target.closest('[data-select]');
        if (!select) return;
        const groups = this.config.groups || [];
        const group = getIndexedGroup(this.indexes, select.dataset.select) || groups.find((entry) => entry.groupTypeKey === select.dataset.select);
        if (!group) return;
        this.state.selections[group.groupTypeKey] = select.value ? [select.value] : [];
        this.state.errorMessage = '';
        this.state.renderMode = 'selection';
        this.renderSelectionState();
        this.dispatchSelectionChange();
      });
    }

    previewGalleryOption(group, optionKey) {
      this.state.galleryPreviewOptionKeys[group.groupTypeKey] = optionKey;
      this.state.previewImageIndex = Math.max(0, getIndexedOptionIndex(this.indexes, group.groupTypeKey, optionKey));
      this.state.errorMessage = '';
      this.state.renderMode = 'preview';
      this.renderPreviewState();
    }

    navigateGalleryOption(direction) {
      this.normalizeRenderState();
      const context = this.getRenderContext();
      if (!context || !context.canNavigateActiveOptions) return;

      const options = (context.gallerySlides || []).filter((slide) => slide.type === 'option' && slide.optionKey);
      const currentOptionIndex = context.galleryPreviewOptionIndex >= 0
        ? context.galleryPreviewOptionIndex
        : (direction === 1 ? -1 : 0);
      const nextOptionIndex = (currentOptionIndex + direction + options.length) % options.length;
      const nextOption = options[nextOptionIndex];
      if (!nextOption) return;

      this.previewGalleryOption(context.activeGroup, nextOption.optionKey);
    }

    getVariantId() {
      return String(
        this.productForm?.querySelector('input[name="id"]')?.value ||
        this.config.product?.baseVariantId ||
        this.dataset.selectedVariantId ||
        ''
      );
    }

    getQuantity() {
      return parseQuantity(getFormQuantityInput(this.productForm)?.value, 1);
    }

    closeModal() {
      const modal = this.closest('modal-dialog');
      if (modal && typeof modal.hide === 'function') {
        modal.hide();
      }
    }

    resetSubmittingState({ render = true } = {}) {
      this.state.isSubmitting = false;
      this.state.submittingAction = '';
      if (render) this.render();
    }

    async waitForInitialAssets() {
      const images = Array.from(this.mountTarget.querySelectorAll('img'));
      const timeout = new Promise((resolve) => window.setTimeout(resolve, 2500));
      await Promise.race([
        Promise.all(images.map((image) => waitForImage(image))),
        timeout,
      ]).catch(() => undefined);
      this.state.assetsLoaded = true;
      this.classList.remove('is-loading-assets');
      this.classList.add('is-assets-loaded');
    }

    async submitCartItem(input = {}) {
      const variantId = String(input.variantId || this.getVariantId());
      if (!variantId) {
        throw new Error('Missing Ajax-compatible variant id for cart add flow.');
      }

      const body = {
        id: variantId,
        quantity: parseCartQuantity(input.quantity || this.getQuantity(), 1),
        properties: input.properties || {},
      };

      if (input.includeSections && input.cart) {
        const sections = getCartSections(input.cart);
        if (sections.length) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
          input.cart.setActiveElement?.(document.activeElement);
        }
      }

      const cartService = window.Express?.cartService;
      if (!cartService || typeof cartService.addItem !== 'function') {
        throw new Error('Cart service is unavailable. Please refresh and try again.');
      }

      return cartService.addItem(body, {
        cartAddUrl: window.routes?.cart_add_url || '/cart/add',
      });
    }

    async submitQuote(input = {}) {
      const quoteDraft = window.Express?.quoteDraft;
      if (!quoteDraft || typeof quoteDraft.addItem !== 'function') {
        throw new Error('Quote draft is unavailable. Please refresh and try again.');
      }

      quoteDraft.addItem(this.getQuoteLineItem(input));

      const redirectUrl =
        input.redirectUrl ||
        this.config.quote?.redirectUrl ||
        this.dataset.quotePageUrl ||
        '/pages/request-a-quote';

      this.redirectTo(redirectUrl);
    }

    async submitAndContinue(input = {}) {
      const cart = getCartInterface();
      const response = await this.submitCartItem({
        ...input,
        cart,
        includeSections: Boolean(cart),
      });

      this.closeModal();

      if (cart && typeof cart.renderContents === 'function' && response?.sections) {
        cart.renderContents(response);
      }
    }

    redirectTo(url) {
      if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '')) {
        return;
      }

      window.location.assign(new URL(url, window.location.origin).toString());
    }

    renderOptionCopy(group, option, config) {
      const badge = option.badge || ((group.recommendedOptionKeys || []).includes(option.key) ? 'Recommended' : '');
      const displayPrice = getOptionDisplayPrice(option, group);

      return `
          <span class="epb__copy">
            <span class="epb__optitle">${escapeHtml(option.label)}<span class="epb__checkmark" aria-hidden="true">${CHECKMARK_ICON}</span></span>
            ${option.shortDescription ? `<span>${escapeHtml(option.shortDescription)}</span>` : ''}
            <span class="epb__price">${escapeHtml(formatDelta(displayPrice.priceDeltaCents, displayPrice.included, config))}</span>
            ${badge ? `<span class="epb__badge">${escapeHtml(badge)}</span>` : ''}
          </span>
        `;
    }

    renderImageCardGroup(group, selections, config, animateOptions) {
      return `
        <div class="epb__opts epb__opts--image-card is-visual${this.state.loaded && !animateOptions ? ' is-loaded' : ''}${animateOptions ? ' is-animating' : ''}">
          ${(group.options || []).map((option, index) => {
        const thumbnail = getThumbnail(option);
        return `
              <button
                type="button"
                class="epb__opt${isSelected(selections, group, option.key) ? ' is-selected' : ''}${thumbnail ? ` has-preview` : ``}"
                data-group="${escapeAttr(group.groupTypeKey)}"
                data-option="${escapeAttr(option.key)}"
                aria-pressed="${isSelected(selections, group, option.key) ? 'true' : 'false'}"
                style="--option-animation-delay:${(0.02 + index * 0.03).toFixed(2)}s"
              >
                ${thumbnail ? `<img class="epb__img" src="${escapeAttr(thumbnail.url)}" alt="${escapeAttr(thumbnail.alt || option.label)}" loading="lazy">` : ''}
                ${this.renderOptionCopy(group, option, config)}
              </button>
            `;
      }).join('')}
        </div>
      `;
    }

    renderPillGroup(group, selections, config, animateOptions) {
      return `
        <div class="epb__opts epb__opts--pill${this.state.loaded && !animateOptions ? ' is-loaded' : ''}${animateOptions ? ' is-animating' : ''}">
          ${(group.options || []).map((option, index) => {
        const selected = isSelected(selections, group, option.key);
        const badge = option.badge || ((group.recommendedOptionKeys || []).includes(option.key) ? 'Recommended' : '');
        const displayPrice = getOptionDisplayPrice(option, group);

        return `
            <button
              type="button"
              class="epb__pill-option${selected ? ' is-selected' : ''}"
              data-group="${escapeAttr(group.groupTypeKey)}"
              data-option="${escapeAttr(option.key)}"
              aria-pressed="${selected ? 'true' : 'false'}"
              style="--option-animation-delay:${(0.02 + index * 0.03).toFixed(2)}s"
            >
              <span class="epb__pill-main">
                <span class="epb__pill-title">${escapeHtml(option.label)}</span>
              </span>
              <span class="epb__pill-meta">
                <span class="epb__price">${escapeHtml(formatDelta(displayPrice.priceDeltaCents, displayPrice.included, config))}</span>
                ${badge ? `<span class="epb__badge">${escapeHtml(badge)}</span>` : ''}
              </span>
              <span class="epb__pill-check" aria-hidden="true">${CHECKMARK_ICON}</span>
            </button>
          `;
      }).join('')}
        </div>
      `;
    }

    renderSwatchGroup(group, selections, config, animateOptions) {
      return `
        <div class="epb__opts epb__opts--swatch${this.state.loaded && !animateOptions ? ' is-loaded' : ''}${animateOptions ? ' is-animating' : ''}">
          ${(group.options || []).map((option, index) => {
        const selected = isSelected(selections, group, option.key);
        const visualImage = getOptionVisualImage(option);
        const badge = option.badge || ((group.recommendedOptionKeys || []).includes(option.key) ? 'Recommended' : '');
        const displayPrice = getOptionDisplayPrice(option, group);

        return `
            <button
              type="button"
              class="epb__swatch-option${selected ? ' is-selected' : ''}${visualImage ? ' has-preview' : ''}"
              data-group="${escapeAttr(group.groupTypeKey)}"
              data-option="${escapeAttr(option.key)}"
              aria-pressed="${selected ? 'true' : 'false'}"
              style="--option-animation-delay:${(0.02 + index * 0.03).toFixed(2)}s"
            >
              <span class="epb__swatch-chip" aria-hidden="true">
                ${visualImage ? `<img src="${escapeAttr(visualImage.url)}" alt="${escapeAttr(visualImage.alt || option.label)}" loading="lazy">` : `<span>${escapeHtml(String(option.label || '').trim().charAt(0) || '?')}</span>`}
              </span>
              <span class="epb__swatch-copy">
                <span class="epb__swatch-title">${escapeHtml(option.label)}<span class="epb__checkmark" aria-hidden="true">${CHECKMARK_ICON}</span></span>
                ${option.shortDescription ? `<span class="epb__swatch-description">${escapeHtml(option.shortDescription)}</span>` : ''}
                <span class="epb__price">${escapeHtml(formatDelta(displayPrice.priceDeltaCents, displayPrice.included, config))}</span>
                ${badge ? `<span class="epb__badge">${escapeHtml(badge)}</span>` : ''}
              </span>
            </button>
          `;
      }).join('')}
        </div>
      `;
    }

    renderGalleryOption(group, option, config, index, context) {
      const thumbnail = getOptionVisualImage(option);
      const selected = isSelected(this.state.selections, group, option.key);
      const previewed = context?.galleryPreviewOptionKey === option.key;
      const displayPrice = getOptionDisplayPrice(option, group);

      return `
        <button
          type="button"
          class="epb__gallery-option${previewed ? ' is-previewed' : ''}${selected ? ' is-selected' : ''}${thumbnail ? ' has-preview' : ''}"
          data-group="${escapeAttr(group.groupTypeKey)}"
          data-option="${escapeAttr(option.key)}"
          data-option-label="${escapeAttr(option.label)}"
          data-gallery-thumbnail
          aria-pressed="${selected ? 'true' : 'false'}"
          aria-current="${previewed ? 'true' : 'false'}"
          aria-label="${escapeAttr(`${selected ? 'Deselect' : 'Select'} ${option.label}`)}"
          style="--option-animation-delay:${(0.02 + index * 0.03).toFixed(2)}s"
        >
          <span class="epb__gallery-select" aria-hidden="true">
            ${CHECKMARK_ICON}
          </span>
          ${thumbnail ? `<img class="epb__gallery-thumb" src="${escapeAttr(thumbnail.url)}" alt="${escapeAttr(thumbnail.alt || option.label)}">` : '<span class="epb__gallery-thumb is-empty"></span>'}
          <span class="epb__gallery-copy">
            <span class="epb__gallery-title">${escapeHtml(option.label)}</span>
            <span class="epb__gallery-price">${escapeHtml(formatDelta(displayPrice.priceDeltaCents, displayPrice.included, config))}</span>
          </span>
        </button>
      `;
    }

    renderGalleryCarousel(context) {
      return `
        <div class="epb__gallery" aria-label="${escapeAttr(context.activeGroup.label)} options">
          <button type="button" class="epb__gallery-scroll epb__gallery-scroll--prev" data-gallery-scroll-dir="prev" aria-label="Scroll gallery options left">
            ${CHEVRON_LEFT}
          </button>
          <div class="epb__gallery-track" aria-label="${escapeAttr(context.activeGroup.label)} thumbnails">
            ${(context.activeGroup.options || []).map((option, index) =>
        this.renderGalleryOption(context.activeGroup, option, this.config, index, context)
      ).join('')}
          </div>
          <button type="button" class="epb__gallery-scroll epb__gallery-scroll--next" data-gallery-scroll-dir="next" aria-label="Scroll gallery options right">
            ${CHEVRON_RIGHT}
          </button>
        </div>
      `;
    }

    getGallerySlideIndex(context) {
      if (context.galleryPreviewOptionKey) {
        return (context.gallerySlides || []).findIndex((slide) => slide.optionKey === context.galleryPreviewOptionKey);
      }

      const fallbackIndex = (context.gallerySlides || []).findIndex((slide) => slide.type === 'fallback');
      return fallbackIndex >= 0 ? fallbackIndex : 0;
    }

    renderGalleryGroup(group, selections, config, animateOptions, context = null) {
      if (!context) this.normalizeRenderState();
      const galleryContext = context || this.getRenderContext();

      return `
        <div class="epb__opts epb__opts--gallery${this.state.loaded && !animateOptions ? ' is-loaded' : ''}${animateOptions ? ' is-animating' : ''}">
          ${galleryContext ? this.renderPreview(galleryContext) : ''}
        </div>
      `;
    }

    renderSelectGroup(group, selections, config, animateOptions) {
      return `
        <div class="epb__opts epb__opts--select${this.state.loaded && !animateOptions ? ' is-loaded' : ''}${animateOptions ? ' is-animating' : ''}">
          ${(group.options || []).map((option, index) => {
        const selected = isSelected(selections, group, option.key);
        const visualImage = getOptionVisualImage(option);
        const badge = option.badge || ((group.recommendedOptionKeys || []).includes(option.key) ? 'Recommended' : '');
        const displayPrice = getOptionDisplayPrice(option, group);

        return `
            <button
              type="button"
              class="epb__select-option${selected ? ' is-selected' : ''}${visualImage ? ' has-preview' : ''}"
              data-group="${escapeAttr(group.groupTypeKey)}"
              data-option="${escapeAttr(option.key)}"
              aria-pressed="${selected ? 'true' : 'false'}"
              style="--option-animation-delay:${(0.02 + index * 0.03).toFixed(2)}s"
            >
              <span class="epb__select-thumb" aria-hidden="true">
                ${visualImage ? `<img src="${escapeAttr(visualImage.url)}" alt="">` : '<span></span>'}
              </span>
              <span class="epb__select-copy">
                <span class="epb__select-title">${escapeHtml(option.label)}</span>
                ${option.shortDescription ? `<span class="epb__select-description">${escapeHtml(option.shortDescription)}</span>` : ''}
                <span class="epb__price">${escapeHtml(formatDelta(displayPrice.priceDeltaCents, displayPrice.included, config))}</span>
                ${badge ? `<span class="epb__badge">${escapeHtml(badge)}</span>` : ''}
                <span class="epb__checkmark" aria-hidden="true">${CHECKMARK_ICON}</span>
              </span>
            </button>
          `;
      }).join('')}
        </div>
      `;
    }

    renderCheckboxGroup(group, selections, config, animateOptions) {
      return `
        <div class="epb__opts epb__opts--checkbox${this.state.loaded && !animateOptions ? ' is-loaded' : ''}${animateOptions ? ' is-animating' : ''}">
          ${(group.options || []).map((option, index) => {
        const selected = isSelected(selections, group, option.key);

        return `
            <button
              type="button"
              class="epb__checkbox-option${selected ? ' is-selected' : ''}"
              data-group="${escapeAttr(group.groupTypeKey)}"
              data-option="${escapeAttr(option.key)}"
              role="checkbox"
              aria-checked="${selected ? 'true' : 'false'}"
              aria-pressed="${selected ? 'true' : 'false'}"
              style="--option-animation-delay:${(0.02 + index * 0.03).toFixed(2)}s"
            >
              <span class="epb__checkbox-box" aria-hidden="true">${CHECKMARK_ICON}</span>
              ${this.renderOptionCopy(group, option, config)}
            </button>
          `;
      }).join('')}
        </div>
      `;
    }

    renderGroup(group, selections, config, animateOptions, context = null) {
      const inputType = getInputType(group);

      if (inputType === 'gallery') return this.renderGalleryGroup(group, selections, config, animateOptions, context);
      if (inputType === 'select') return this.renderSelectGroup(group, selections, config, animateOptions);
      if (inputType === 'checkbox') return this.renderCheckboxGroup(group, selections, config, animateOptions);
      if (inputType === 'pill') return this.renderPillGroup(group, selections, config, animateOptions);
      if (inputType === 'swatch') return this.renderSwatchGroup(group, selections, config, animateOptions);
      return this.renderImageCardGroup(group, selections, config, animateOptions);
    }

    async handleSubmitAction(action = 'add-to-quote') {
      const groups = this.config.groups || [];

      if (this.state.isSubmitting) return;
      if (!hasRequiredSelections(groups, this.state.selections)) {
        this.state.errorMessage = 'Select the required options before continuing.';
        this.render();
        return;
      }

      this.state.isSubmitting = true;
      this.state.submittingAction = action;
      this.state.errorMessage = '';
      this.render();

      try {
        const selectedOptions = collectSelectedOptions(groups, this.state.selections, this.indexes);
        const total = getSelectedOptionsTotal(this.config, selectedOptions);
        const properties = buildProperties(this.config, selectedOptions, total);

        const input = {
          variantId: this.config.product?.baseVariantId || this.getVariantId(),
          redirectUrl: this.config.quote?.redirectUrl || this.dataset.quotePageUrl,
          properties,
        };

        if (action === 'save') {
          syncHiddenInputs(this.productForm, properties);
          this.dispatchConfigurationSaved({ selectedOptions, total, properties });
          this.closeModal();
          this.resetSubmittingState();
        } else if (action === 'add-to-cart') {
          syncHiddenInputs(this.productForm, properties);
          await this.submitAndContinue(input);
          this.resetSubmittingState();
        } else if (action === 'update-quote') {
          this.dispatchEvent(new CustomEvent('product-builder:quote-update', {
            bubbles: true,
            detail: {
              lineItem: this.getQuoteLineItem(input),
            },
          }));
          this.resetSubmittingState();
        } else {
          syncHiddenInputs(this.productForm, properties);
          await this.submitQuote(input);
        }
      } catch (error) {
        this.state.isSubmitting = false;
        this.state.submittingAction = '';
        this.state.errorMessage = error instanceof Error
          ? error.message
          : 'Unable to add this configuration to the quote.';
        this.render();
      }
    }

    getBuilderGroups() {
      return this.config.groups || [];
    }

    getActiveRenderGroup(groups) {
      return getIndexedGroup(this.indexes, this.state.activeGroupKey) || groups[0] || null;
    }

    normalizeRenderState() {
      const groups = this.getBuilderGroups();
      if (groups.length === 0) return;

      const activeGroup = this.getActiveRenderGroup(groups);
      if (activeGroup) {
        this.state.activeGroupKey = activeGroup.groupTypeKey;
      }
      this.state.previewImageIndex = 0;
    }

    getGalleryRenderContext(activeGroup, activeInputType, activeOptionKey, activeSelectedOption, previewHelper) {
      const storedGalleryPreviewOptionKey = this.state.galleryPreviewOptionKeys[activeGroup.groupTypeKey] || '';
      const storedGalleryPreviewOption = getIndexedOption(this.indexes, activeGroup.groupTypeKey, storedGalleryPreviewOptionKey);
      const galleryPreviewOptionKey = activeInputType === 'gallery'
        ? (storedGalleryPreviewOption?.key || activeOptionKey)
        : '';
      const previewSelectedOption = activeInputType === 'gallery'
        ? getIndexedOption(this.indexes, activeGroup.groupTypeKey, galleryPreviewOptionKey)
        : activeSelectedOption;
      const gallerySlides = activeInputType === 'gallery'
        ? this.getGalleryPreviewSlides(activeGroup, previewHelper)
        : [];
      const galleryOptionSlides = gallerySlides.filter((slide) => slide.type === 'option' && slide.optionKey);
      const galleryPreviewOptionIndex = galleryOptionSlides.findIndex((slide) => slide.optionKey === galleryPreviewOptionKey);
      const canNavigateActiveOptions = activeInputType === 'gallery'
        && activeGroup.selectionMode !== 'multiple'
        && galleryOptionSlides.length > 1;
      const galleryHasFallbackSlide = gallerySlides.some((slide) => slide.type === 'fallback');

      return {
        canNavigateActiveOptions,
        galleryHasFallbackSlide,
        galleryPreviewOptionIndex,
        galleryPreviewOptionKey,
        gallerySlides,
        previewSelectedOption,
      };
    }

    getPreviewRenderContext(activeGroup, activeInputType, selectedOptions, previewHelper, galleryContext) {
      const previewOption = previewHelper.getPreviewOption(activeGroup, this.state.selections, selectedOptions);
      const previewImages = previewHelper.getPreviewImages(this.config, selectedOptions);
      let previewImageIndex = previewHelper.normalizeIndex(this.state.previewImageIndex, previewImages);
      let previewImage = previewImages[previewImageIndex] || null;
      const activePreviewImages = galleryContext.previewSelectedOption
        ? previewHelper.getOptionPreviewImages({
          ...galleryContext.previewSelectedOption,
          groupKey: activeGroup.groupTypeKey,
          groupLabel: activeGroup.label,
        })
        : [];
      const activeGroupHasPreview = !galleryContext.previewSelectedOption && (activeGroup.options || []).some((option) =>
        previewHelper.getOptionPreviewImages({
          ...option,
          groupKey: activeGroup.groupTypeKey,
          groupLabel: activeGroup.label,
        }).length > 0
      );

      previewImageIndex = 0;
      previewImage = activePreviewImages[0] || (activeGroupHasPreview ? getProductPreviewImage(this.config) : null);
      if (activeInputType === 'gallery') {
        const galleryPreviewSlide = galleryContext.galleryPreviewOptionKey
          ? galleryContext.gallerySlides.find((slide) => slide.optionKey === galleryContext.galleryPreviewOptionKey) || null
          : null;
        previewImage = galleryPreviewSlide
          || galleryContext.gallerySlides.find((slide) => slide.type === 'fallback')
          || galleryContext.gallerySlides[0]
          || null;
      }

      return {
        previewImage,
        previewImageIndex,
        previewImages,
        previewOption,
        previewUrl: previewImage?.url || '',
      };
    }

    getRenderContext() {
      const groups = this.getBuilderGroups();
      if (groups.length === 0) {
        return null;
      }

      const activeGroup = this.getActiveRenderGroup(groups);
      if (!activeGroup) return null;

      const activeInputType = getInputType(activeGroup);
      const activeGroupIndex = getActiveGroupIndex(groups, activeGroup.groupTypeKey, this.indexes);
      const activeOptionIndex = getSelectedOptionIndex(activeGroup, this.state.selections, this.indexes);

      const selectedOptions = collectSelectedOptions(groups, this.state.selections, this.indexes);
      const stepCount = activeGroupIndex + 1;
      const total = getSelectedOptionsTotal(this.config, selectedOptions);
      const activeOptionKey = (this.state.selections[activeGroup.groupTypeKey] || [])[0] || '';
      const activeSelectedOption = getSelectedOption(activeGroup, this.state.selections, this.indexes);
      const previewHelper = getPreviewHelper();
      const galleryContext = this.getGalleryRenderContext(
        activeGroup,
        activeInputType,
        activeOptionKey,
        activeSelectedOption,
        previewHelper
      );
      const previewContext = this.getPreviewRenderContext(activeGroup, activeInputType, selectedOptions, previewHelper, galleryContext);
      const shouldAnimatePreview = (this.state.renderMode === 'selection' || this.state.renderMode === 'section')
        && Boolean(previewContext.previewUrl)
        && Boolean(this.state.lastPreviewUrl)
        && this.state.lastPreviewUrl !== previewContext.previewUrl;
      const shouldAnimateSection = this.state.renderMode !== 'selection';
      const shouldAnimateTotal = this.state.lastTotal >= 0 && this.state.lastTotal === total;
      const submitDisabled = this.state.isSubmitting || !hasRequiredSelections(groups, this.state.selections);
      const properties = buildProperties(this.config, selectedOptions, total);

      return {
        activeGroup,
        activeGroupIndex,
        activeInputType,
        activeOptionKey,
        activeOptionIndex,
        canNavigateActiveOptions: galleryContext.canNavigateActiveOptions,
        galleryHasFallbackSlide: galleryContext.galleryHasFallbackSlide,
        galleryPreviewOptionIndex: galleryContext.galleryPreviewOptionIndex,
        galleryPreviewOptionKey: galleryContext.galleryPreviewOptionKey,
        gallerySlides: galleryContext.gallerySlides,
        groups,
        previewImage: previewContext.previewImage,
        previewImageIndex: previewContext.previewImageIndex,
        previewImages: previewContext.previewImages,
        previewOption: previewContext.previewOption,
        previewUrl: previewContext.previewUrl,
        properties,
        selectedOptions,
        shouldAnimatePreview,
        shouldAnimateSection,
        shouldAnimateTotal,
        submitDisabled,
        stepCount,
        total,
      };
    }

    renderNav(groups, activeGroup) {
      return groups.map((group) => `
        <button type="button" class="epb__navitem${(this.state.selections[group.groupTypeKey] || []).length > 0 ? ' checked' : ''}${group.groupTypeKey === activeGroup.groupTypeKey ? ' is-active' : ''}${group.groupTypeKey === this.state.navAnimationGroupKey ? ' is-nav-animating' : ''}" data-nav="${escapeAttr(group.groupTypeKey)}">
          <span class="epb__navlabel">${escapeHtml(group.label)}</span>
          <span class="epb__navstate">${(this.state.selections[group.groupTypeKey] || []).length > 0 ? CHECKMARK_ICON : ''}</span>
        </button>
      `).join('');
    }

    renderActiveGroupTitle(context) {
      return `
        <div>
          <h3>Choose ${escapeHtml(context.activeGroup.label)}</h3>
          <p>${escapeHtml(context.activeGroup.helpText || 'Select your preferred option')}</p>
        </div>
      `;
    }

    renderActiveGroupHeader(context) {
      return `
        <header class="epb__head${context.shouldAnimateSection ? ' is-animating' : ''}" style="display:flex;justify-content:space-between;flex-direction:row;">
          ${this.renderActiveGroupTitle(context)}
          <div>
          <button type="button" class="epb__button-next mobile-hidden" data-nav-dir="next" ${context.activeGroupIndex === context.groups.length - 1 ? 'disabled' : ''} aria-label="Next option group">
            Next ${CHEVRON_RIGHT}
          </button>
          </div>
        </header>
      `;
    }

    renderPreview(context) {
      if (context.activeInputType !== 'gallery') return '';

      const galleryCarousel = context.activeInputType === 'gallery'
        ? this.renderGalleryCarousel(context)
        : '';
      const gallerySlides = context.gallerySlides || [];
      const activeGallerySlideIndex = Math.max(0, this.getGallerySlideIndex(context));
      const showPreviewDots = this.config.ui?.showPreviewDots !== false;

      if (!context.previewUrl) {
        return `
          <div class="epb__preview-panel mobile-hidden${context.activeInputType === 'gallery' ? ' is-gallery' : ''}">
            <div class="epb__preview-frame">
              <span class="epb__preview-empty"></span>
              ${galleryCarousel}
            </div>
          </div>
        `;
      }

      const canNavigatePreviewOptions = context.canNavigateActiveOptions;

      return `
      <div class="epb__preview-panel${context.activeInputType === 'gallery' ? ' is-gallery' : ''}" style="--gallery-slide-index:${activeGallerySlideIndex}">
        <div class="epb__preview-frame">
          ${canNavigatePreviewOptions ? `
            <button type="button" class="epb__option-nav epb__option-nav--prev" data-option-preview-dir="prev" aria-label="Previous option">
              ${CHEVRON_LEFT}
            </button>
            <button type="button" class="epb__option-nav epb__option-nav--next" data-option-preview-dir="next" aria-label="Next option">
              ${CHEVRON_RIGHT}
            </button>
            ${showPreviewDots ? `<div class="epb__preview-dots" aria-hidden="true">
              ${gallerySlides.map((_slide, index) => `
                <span class="epb__preview-dot${index === activeGallerySlideIndex ? ' is-active' : ''}" data-gallery-dot></span>
              `).join('')}
            </div>` : ''}
          ` : ''}
          <div
            class="epb__gallery-stage"
            role="region"
            aria-label="${escapeAttr(`${context.activeGroup.label} preview`)}"
          >
            <div class="epb__gallery-slides">
              ${gallerySlides.map((slide, index) => `
                <div
                  class="epb__gallery-slide${index === activeGallerySlideIndex ? ' is-active' : ''}${slide.optionKey && slide.optionKey === context.activeOptionKey ? ' is-selected' : ''}"
                  data-gallery-slide
                  data-option="${escapeAttr(slide.optionKey || '')}"
                  draggable="false"
                  role="group"
                  aria-label="${escapeAttr(`${index + 1} of ${gallerySlides.length}`)}"
                  aria-hidden="${index === activeGallerySlideIndex ? 'false' : 'true'}"
                >
                  <div class="epb__preview-image-wrapper${slide.optionKey && slide.optionKey === context.activeOptionKey ? ' is-selected' : ''}">
                    <img
                      class="epb__preview"
                      src="${escapeAttr(slide.url)}"
                      alt="${escapeAttr(slide.alt)}"
                      draggable="false"
                    >
                    <div class="epb__preview-title">
                      <h3>${slide.groupLabel && slide.optionLabel ? `<strong>${escapeHtml(slide.groupLabel)}</strong>: ${escapeHtml(slide.optionLabel)}` : escapeHtml(slide.title || slide.alt || 'Preview')}</h3>
                    </div>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
          </div>
          ${galleryCarousel}
      </div>
      `;
    }


    renderSumlinesTitle() {
      return `
        <div class="epb__sumlines-title desktop-hiddden">
          <h3>Your Configuration</h3>
        </div>
      `;
    }

    renderSumlinePrice(option) {
      const displayPrice = getOptionDisplayPrice(option);

      return formatDelta(displayPrice.priceDeltaCents, displayPrice.included, this.config);
    }

    renderSumlinesList(context) {
      return `
        <div class="epb__sumlines">
          <div class="epb__sumline">
            <div>
              <div class="epb__sumline-selected-opt-group-label">Base price</div>
              <span class="epb__sumline-selected-opt-label truncate-text mobile-hidden" style="max-width: 20ch;line-height: 1;">
                ${escapeHtml(this.config.product?.title || this.config.ui?.title || this.config.ui?.summaryTitle || 'Your Configuration')}
              </span>
            </div>
            <strong>${escapeHtml(formatMoney(Number(this.config.product?.basePriceCents || 0), this.config))}</strong>
          </div>
          ${context.selectedOptions.map((option) => `
            <button
              type="button"
              class="epb__sumline${option.groupKey === context.activeGroup.groupTypeKey ? ' is-active is-animating' : ''}"
              data-summary-nav="${escapeAttr(option.groupKey)}"
              aria-current="${option.groupKey === context.activeGroup.groupTypeKey ? 'step' : 'false'}"
              aria-label="${escapeAttr(`Go to ${option.groupLabel}`)}"
            >
              <div>
                <div class="epb__sumline-selected-opt-group-label">${escapeHtml(option.groupLabel)}</div>
                <span class="epb__sumline-selected-opt-label">${escapeHtml(option.label)}</span>
              </div>
              <strong>${escapeHtml(this.renderSumlinePrice(option))}</strong>
            </button>
          `).join('')}
        </div>
      `;
    }

    renderSumlines(context) {
      return `
        ${this.renderSumlinesTitle()}
        ${this.renderSumlinesList(context)}
      `;
    }

    renderActionButtons(context) {
      const submitAction = this.isQuoteEditMode() ? 'update-quote' : 'save';
      const submitLabel = this.state.isSubmitting && this.state.submittingAction === submitAction ? 'Saving...' : 'Save';

      return `
        <button type="button" class="epb__submit" data-submit data-submit-action="${submitAction}" ${context.submitDisabled ? 'disabled' : ''}>${submitLabel}</button>
        <button type="button" class="button button--small epb__button-add-to-cart button--tertiary" data-builder-cancel>
          Cancel
        </button>
      `;
    }

    renderSummary(context) {
      return `
        <aside class="epb__sum">
          ${this.renderSumlinesTitle()}
          <div class="epb__sumlines-scroll">
            ${this.renderSumlinesList(context)}
            ${this.state.errorMessage ? `<div class="epb__error">${escapeHtml(this.state.errorMessage)}</div>` : ''}
          </div>
          <div class="epb__sumfoot">
            <div class="mobile-hidden">
              <div class="epb__sumlines">
                <div class="epb__sumline is-total"><span>Total</span><strong class="epb__fade-up ${context.shouldAnimateTotal ? 'is-animating' : ''}">${escapeHtml(formatMoney(context.total, this.config))}</strong></div>
              </div>
              <p class="epb__footcopy">${escapeHtml(this.config.ui?.summaryNote || this.config.notes || CONFIG_NOTES_DEFAULT)}</p>
            </div>
            ${this.renderActionButtons(context)}
          </div>
        </aside>
      `;
    }

    renderFooter(context) {
      const productThumbnailUrl = getProductThumbnailUrl(this.config.product);
      const expanded = Boolean(this.state.footerSumlinesExpanded);
      const footerSumlinesId = this.getFooterSumlinesId();

      return `
        <div class="epb__footer-wrapper">
          <div class="epb__footer">
            <div class="epb__footer-heading">
              <div class="epb__nav-arrows">
                <button type="button" class="epb__nav-arrow epb__nav-prev" data-nav-dir="prev" ${context.activeGroupIndex === 0 ? 'disabled' : ''} aria-label="Previous option group">
                  ${CHEVRON_LEFT}
                </button>
                <div class="epb__meta">${context.stepCount} of ${context.groups.length}</div>
                <button type="button" class="epb__nav-arrow epb__nav-next" data-nav-dir="next" ${context.activeGroupIndex === context.groups.length - 1 ? 'disabled' : ''} aria-label="Next option group">
                  ${CHEVRON_RIGHT}
                </button>
              </div>
              <button type="button" class="epb__nav-arrow epb__footer-arrow epb__footer-show-sumlines${expanded ? ' is-expanded' : ''}" data-footer-sumlines-toggle aria-controls="${escapeAttr(footerSumlinesId)}" aria-expanded="${expanded ? 'true' : 'false'}" aria-label="${expanded ? 'Hide summary items' : 'Show summary items'}">
                ${expanded ? CHEVRON_DOWN : CHEVRON_UP}
              </button>
            </div>
            <div id="${escapeAttr(footerSumlinesId)}" class="epb__footer-sumlines${expanded ? ' is-expanded' : ''}" aria-hidden="${expanded ? 'false' : 'true'}">
              <div class="epb__footer-sumlines-inner">
                <div class="epb__product-title">
                  ${productThumbnailUrl ? `<img class="epb__product-thumb" src="${escapeAttr(productThumbnailUrl)}" alt="${escapeAttr(this.config.product?.title || 'Product')}">` : ''}
                  <h3>${escapeHtml(this.config.product?.title || this.config.ui?.title || this.config.ui?.summaryTitle || 'Your Configuration')}</h3>
                </div>
                ${this.renderSumlinesTitle()}
                <div class="epb__footer-sumlines-scroll">
                  ${this.renderSumlinesList(context)}
                </div>
              </div>
            </div>
            <div class="epb__product-price-wrapper">
              <div class="epb__sumlines">
                <div class="epb__sumline is-total">
                  <div>
                    <span>Total</span>
                    <p class="epb__sumcopy">${escapeHtml(this.config.ui?.summaryNote || this.config.notes || CONFIG_NOTES_DEFAULT)}</p>
                  </div>
                  <strong class="epb__fade-up ${context.shouldAnimateTotal ? 'is-animating' : ''}">${escapeHtml(formatMoney(context.total, this.config))}</strong>
                </div>
              </div>
            </div>
            <div class="epb__footer-actions">
              ${this.renderActionButtons(context)}
            </div>
          </div>
        </div>
      `;
    }

    syncRenderState(context) {
      updateThemePrice(this, context.total);

    }

    syncNavigationState(context, input = {}) {
      const scrollActiveNavItem = Boolean(input.scrollActiveNavItem);
      const meta = this.mountTarget.querySelector('.epb__meta');
      if (meta) meta.textContent = `${context.stepCount} of ${context.groups.length}`;

      const prevArrow = this.mountTarget.querySelector('[data-nav-dir="prev"]');
      if (prevArrow) prevArrow.disabled = context.activeGroupIndex === 0;

      const nextArrow = this.mountTarget.querySelector('[data-nav-dir="next"]');
      if (nextArrow) nextArrow.disabled = context.activeGroupIndex === context.groups.length - 1;

      const navItems = this.mountTarget.querySelectorAll('[data-nav]');
      navItems.forEach((item) => {
        const group = getIndexedGroup(this.indexes, item.dataset.nav) || context.groups.find((entry) => entry.groupTypeKey === item.dataset.nav);
        if (!group) return;

        const isActive = group.groupTypeKey === context.activeGroup.groupTypeKey;
        const isChecked = (this.state.selections[group.groupTypeKey] || []).length > 0;
        item.classList.toggle('is-active', isActive);
        item.classList.toggle('checked', isChecked);
        item.classList.toggle('is-nav-animating', group.groupTypeKey === this.state.navAnimationGroupKey);

        const navState = item.querySelector('.epb__navstate');
        if (navState) navState.innerHTML = isChecked ? CHECKMARK_ICON : '';

      });

      this.syncNavScroll({ centerActive: scrollActiveNavItem });
    }

    renderPreviewPanelState(context) {
      const previewPanel = this.mountTarget.querySelector('.epb__preview-panel');
      if (context.activeInputType !== 'gallery') {
        if (previewPanel) previewPanel.remove();
        return;
      }

      if (previewPanel) {
        this.syncGalleryState(context);
        return;
      }

      const galleryGroup = this.mountTarget.querySelector('.epb__opts--gallery');
      if (galleryGroup) {
        galleryGroup.insertAdjacentHTML('afterbegin', this.renderPreview(context));
        this.syncGalleryState(context);
      }
    }

    renderErrorMessage(message) {
      this.mountTarget.innerHTML = `<div class="ep-product-builder-status is-error">${escapeHtml(message)}</div>`;
    }

    renderActiveGroupState() {
      this.normalizeRenderState();
      const context = this.getRenderContext();
      if (!context) {
        this.renderErrorMessage(MISSING_BUILDER_CONFIG_MESSAGE);
        return;
      }

      this.syncRenderState(context);
      this.syncNavigationState(context, { scrollActiveNavItem: true });

      const header = this.mountTarget.querySelector('.epb__head');
      if (header) {
        header.outerHTML = this.renderActiveGroupHeader(context);
      }

      const optionGroup = this.mountTarget.querySelector('.epb__opts');
      if (optionGroup) {
        optionGroup.outerHTML = this.renderGroup(context.activeGroup, this.state.selections, this.config, true, context);
      }

      this.renderPreviewPanelState(context);

      const summary = this.mountTarget.querySelector('.epb__sum');
      if (summary) {
        summary.outerHTML = this.renderSummary(context);
      }

      const footer = this.mountTarget.querySelector('.epb__footer-wrapper');
      if (footer) {
        footer.outerHTML = this.renderFooter(context);
      }

      this.state.lastPreviewOption = context.previewOption;
      this.state.lastPreviewUrl = context.previewUrl;
      this.state.lastTotal = context.total;
      this.state.loaded = true;
      this.state.navAnimationGroupKey = null;
    }

    renderPreviewState() {
      this.normalizeRenderState();
      const context = this.getRenderContext();
      if (!context) {
        this.renderErrorMessage(MISSING_BUILDER_CONFIG_MESSAGE);
        return;
      }

      this.renderPreviewPanelState(context);

      this.state.lastPreviewOption = context.previewOption;
      this.state.lastPreviewUrl = context.previewUrl;
      this.state.navAnimationGroupKey = null;
    }

    renderSelectionState() {
      this.normalizeRenderState();
      const context = this.getRenderContext();
      if (!context) {
        this.renderErrorMessage(MISSING_BUILDER_CONFIG_MESSAGE);
        return;
      }

      this.syncRenderState(context);
      this.syncNavigationState(context);

      const optionGroup = this.mountTarget.querySelector('.epb__opts');
      if (optionGroup) {
        optionGroup.classList.add('is-loaded');
        optionGroup.classList.remove('is-animating');
      }

      if (context.activeInputType !== 'gallery') {
        const activeButtons = this.mountTarget.querySelectorAll(`[data-group="${CSS.escape(context.activeGroup.groupTypeKey)}"][data-option]`);
        activeButtons.forEach((button) => {
          const isOptionSelected = isSelected(this.state.selections, context.activeGroup, button.dataset.option || '');
          button.classList.toggle('is-selected', isOptionSelected);
          button.setAttribute('aria-pressed', isOptionSelected ? 'true' : 'false');
          if (button.hasAttribute('aria-checked')) {
            button.setAttribute('aria-checked', isOptionSelected ? 'true' : 'false');
          }
        });
      }
      this.state.loaded = true;

      this.renderPreviewPanelState(context);

      const summary = this.mountTarget.querySelector('.epb__sum');
      if (summary) {
        summary.outerHTML = this.renderSummary(context);
      }

      const footer = this.mountTarget.querySelector('.epb__footer-wrapper');
      if (footer) {
        footer.outerHTML = this.renderFooter(context);
      }

      this.state.lastPreviewOption = context.previewOption;
      this.state.lastPreviewUrl = context.previewUrl;
      this.state.lastTotal = context.total;
      this.state.loaded = true;
      this.state.navAnimationGroupKey = null;
    }

    render() {
      this.normalizeRenderState();
      const context = this.getRenderContext();
      if (!context) {
        this.renderErrorMessage(MISSING_BUILDER_CONFIG_MESSAGE);
        return;
      }

      this.syncRenderState(context);
      const productThumbnailUrl = getProductThumbnailUrl(this.config.product);
      const headerLogo = this.headerLogoHtml
        ? `<span class="epb__brand-logo-wrap" aria-hidden="true">${this.headerLogoHtml}</span>`
        : '';
      const mobileTitleText = escapeHtml(this.config.ui?.title || this.config.product?.title || 'Customiser');
      const mobileHeaderTitle = headerLogo
        ? `${headerLogo}<span class="visually-hidden">${mobileTitleText}</span>`
        : `<span>${mobileTitleText}</span>`;

      this.mountTarget.innerHTML = `
        <div class="epb">
          <div class="epb__header">
            <div class="epb__product-title">
              <h2 class="epb__header-title">${mobileHeaderTitle}</h2>
            </div>
            <div style="text-align: center;">
              <h2 class="epb__header-title epb__header-mobile-hidden">
                <span class="mobile-hidden">${escapeHtml(this.config.ui?.title || this.config.ui?.summaryTitle || 'Build your own configuration')}</span>
              </h2>
            </div>
            <div class="epb__nav-actions">
            </div>
          </div>
          <div style="position:absolute;inset:0;margin: auto;max-width:50vw;max-height:80vh;z-index:20;display:flex;flex-direction:column;align-items:center;justify-content:center;background:white;padding:2rem;border-radius:1rem;display:none;">
            <img style="width:100%;aspect-ratio:3/2;max-height:50vh;object-fit:contain;" src="${escapeAttr(this.config.product?.product_image?.url)}" alt="${escapeAttr(this.config.product?.title || 'Product')}">
            <h1 style="text-align:center;margin-bottom:0;">${mobileTitleText}</h1>
            <p style="text-align:center;margin-top:0;">${escapeHtml(this.config.ui?.subtitle)}</p>
            <div style="display:flex;justify-content:center">
              <button type="button" class="epb__button-next btn-primary" data-nav-dir="next" ${context.activeGroupIndex === context.groups.length - 1 ? 'disabled' : ''} aria-label="Next option group">
                Start ${CHEVRON_RIGHT}
              </button>
            </div>
          </div>
          <div class="epb__shell">
            <nav class="epb__nav">
              <button type="button" class="epb__nav-scroll epb__nav-scroll--prev" data-nav-scroll-dir="prev" aria-label="Scroll option groups left">
                ${CHEVRON_LEFT}
              </button>
              <div class="epb__navlist">${this.renderNav(context.groups, context.activeGroup)}</div>
              <button type="button" class="epb__nav-scroll epb__nav-scroll--next" data-nav-scroll-dir="next" aria-label="Scroll option groups right">
                ${CHEVRON_RIGHT}
              </button>
            </nav>
            <main class="epb__main">
              ${this.renderActiveGroupHeader(context)}
              ${this.renderGroup(context.activeGroup, this.state.selections, this.config, this.state.renderMode !== 'selection', context)}
            </main>
            <div class="epb__sum-track">
              ${this.renderSummary(context)}
            </div>
          </div>
          ${this.renderFooter(context)}
        </div>
      `;

      this.state.lastPreviewOption = context.previewOption;
      this.state.lastPreviewUrl = context.previewUrl;
      this.state.lastTotal = context.total;
      this.state.loaded = true;
      this.state.navAnimationGroupKey = null;
      this.bindDraggableNavigation();
      this.syncNavScroll({ centerActive: true });
      this.syncGalleryState(context);
    }
  }

  if (!customElements.get('express-product-builder')) {
    customElements.define('express-product-builder', ExpressProductBuilder);
  }

  window.Express = window.Express || {};
  if (!window.Express.productBuilderExternalQuoteListenerBound) {
    window.Express.productBuilderExternalQuoteListenerBound = true;
    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-product-builder-quote-form]');
      if (!form) return;

      event.preventDefault();

      const builderId = form.dataset.productBuilderId || '';
      const builder = builderId
        ? document.getElementById(builderId)
        : form.closest('[data-product-builder-scope]')?.querySelector('express-product-builder');

      if (!builder || typeof builder.submitExternalQuote !== 'function') return;

      builder.submitExternalQuote().catch((error) => console.error(error));
    });
  }
})();
